/**
 * Bespoke "drafting" icon set for the left rail — the signature, always-visible
 * navigation. Hand-drawn on a 24px grid, 1.6 stroke, rounded joins, tuned to the
 * furniture domain (a facade with handles, stacked drawers, a hinge, a sketch
 * upload) so the rail reads as a workshop instrument rather than a generic app.
 * The long tail of utility icons stays on lucide.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { className?: string };

function Svg({ children, className, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconStructure(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <rect x="3" y="17" width="6" height="4" rx="1" />
      <rect x="15" y="17" width="6" height="4" rx="1" />
      <path d="M12 7v4M6 17v-2h12v2M12 11v4" />
    </Svg>
  );
}

export function IconModels(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 3 4 7v10l8 4 8-4V7z" />
      <path d="M4 7l8 4 8-4M12 11v10" />
    </Svg>
  );
}

export function IconLibrary(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
      <path d="M17.5 14.5v6M14.5 17.5h6" />
    </Svg>
  );
}

export function IconCatalog(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="4" width="17" height="16" rx="1.5" />
      <path d="M3.5 9h17M9 9v11" />
      <path d="M13 13h4M13 16h4" />
    </Svg>
  );
}

export function IconTemplates(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 3v18M8 9h12" />
    </Svg>
  );
}

export function IconFacades(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="5" y="3" width="14" height="18" rx="1.2" />
      <path d="M12 3v18" />
      <path d="M9.4 11v2M14.6 11v2" />
    </Svg>
  );
}

export function IconDrawers(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="5" rx="1" />
      <rect x="4" y="10" width="16" height="5" rx="1" />
      <rect x="4" y="16" width="16" height="4" rx="1" />
      <path d="M10 6.5h4M10 12.5h4" />
    </Svg>
  );
}

export function IconConstructive(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <path d="M4 8h16M8 8v12" />
    </Svg>
  );
}

export function IconFill(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 12h18M12 3v18" />
      <circle cx="7.5" cy="7.5" r="1.3" />
      <path d="M15 15.5h3M15 18h3" />
    </Svg>
  );
}

export function IconConstructor(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 20h16M6 20V9l6-5 6 5v11" />
      <path d="M10 20v-5h4v5" />
    </Svg>
  );
}

export function IconLayout(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5" width="5" height="5" rx="1" />
      <rect x="16" y="5" width="5" height="5" rx="1" />
      <rect x="9.5" y="15" width="5" height="5" rx="1" />
      <path d="M8 7.5h8M12 10v5M5.5 13v-1.5M18.5 13v-1.5" />
    </Svg>
  );
}

export function IconOrders(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </Svg>
  );
}

export function IconMaterials(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="9" r="5" />
      <path d="M13 5a6.4 6.4 0 0 1 0 14M13 4.6a7.6 7.6 0 0 1 0 14.8" />
    </Svg>
  );
}

export function IconHardware(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8 4v6a3 3 0 0 0 3 3h1" />
      <path d="M16 20v-6a3 3 0 0 0-3-3h-1" />
      <circle cx="8" cy="4" r="1.7" />
      <circle cx="16" cy="20" r="1.7" />
    </Svg>
  );
}

export function IconUpload(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 15V4M8 8l4-4 4 4" />
      <path d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
    </Svg>
  );
}
